export const INVESTIGATION_CATEGORIES = {
  BLOOD_PRESSURE: {
    label: 'Blood Pressure',
    icon: '🩸',
    items: ['BP Monitoring', 'Ambulatory Blood Pressure (ABPM)', 'Home Blood Pressure (HBPM)']
  },
  BIOCHEMICAL: {
    label: 'Biochemical',
    icon: '🧪',
    items: [
      'Blood Sugar (RBS/FBS/PPBS)',
      'Lipid Profile',
      'Liver Function Test (LFT)',
      'Kidney Function Test (KFT / RFT)',
      'Electrolytes (Na, K, Cl)',
      'Glycated Hemoglobin (HbA1c)',
      'Complete Blood Count (CBC)',
      'Hemoglobin (Hb)',
      'Platelet Count',
      'Thyroid Profile (TFT)',
      'Thyroid Stimulating Hormone (TSH)',
      'Triiodothyronine (T3)',
      'Thyroxine (T4)'
    ]
  },
  RADIOLOGICAL: {
    label: 'Radiological',
    icon: '🩻',
    items: ['X-Ray', 'Chest X-Ray (CXR)', 'Bone X-Ray']
  },
  ULTRASOUND: {
    label: 'Ultrasound',
    icon: '📊',
    items: ['Abdominal Ultrasound (USG)', 'Pelvic Ultrasound (USG)', 'Echocardiogram (ECHO)', 'Obstetric Ultrasound (USG)']
  },
  CT_SCAN: {
    label: 'CT Scan',
    icon: '💠',
    items: ['CT Head (NCCT / CECT)', 'CT Chest (HRCT)', 'CT Abdomen', 'CT Spine']
  },
  ECG: {
    label: 'ECG',
    icon: '❤️',
    items: ['Resting ECG', 'Stress ECG (TMT)', '24h ECG (Holter)']
  },
  THYROID: {
    label: 'Thyroid',
    icon: '🦋',
    items: ['Thyroid Profile (TFT)', 'Thyroid Stimulating Hormone (TSH)', 'Triiodothyronine (T3)', 'Thyroxine (T4)']
  },
  MRI: {
    label: 'MRI',
    icon: '🧠',
    items: ['MRI Brain', 'MRI Spine', 'MRI Joints']
  },
  OTHER: {
    label: 'Other',
    icon: '📋',
    items: ['Other Investigations']
  }
};

export const ALL_INVESTIGATIONS = Object.entries(INVESTIGATION_CATEGORIES).flatMap(
  ([key, category]) =>
    category.items.map((item) => ({
      id: `${key}_${item.replace(/[\s/()]+/g, '_').replace(/[^a-zA-Z0-9_]/g, '')}`,
      label: item,
      category: category.label,
      icon: category.icon,
      categoryKey: key
    }))
);

export const FREQUENCY_OPTIONS = [
  { value: 'once_daily', label: 'Once daily' },
  { value: 'twice_daily', label: 'Twice daily' },
  { value: 'three_times_daily', label: 'Three times daily' },
  { value: 'four_times_daily', label: 'Four times daily' },
  { value: 'before_breakfast', label: 'Before breakfast' },
  { value: 'after_breakfast', label: 'After breakfast' },
  { value: 'before_lunch', label: 'Before lunch' },
  { value: 'after_lunch', label: 'After lunch' },
  { value: 'before_dinner', label: 'Before dinner' },
  { value: 'after_dinner', label: 'After dinner' },
  { value: 'morning', label: 'In the morning' },
  { value: 'afternoon', label: 'In the afternoon' },
  { value: 'evening', label: 'In the evening' },
  { value: 'night', label: 'At night' },
  { value: 'bedtime', label: 'At bedtime' },
  { value: 'as_needed', label: 'As needed (PRN)' },
  { value: 'every_4_hours', label: 'Every 4 hours' },
  { value: 'every_6_hours', label: 'Every 6 hours' },
  { value: 'every_8_hours', label: 'Every 8 hours' },
  { value: 'every_12_hours', label: 'Every 12 hours' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'other', label: 'Other (custom)' }
];

export const getFilteredInvestigations = (searchTerm) => {
  const term = searchTerm.toLowerCase().trim();
  if (!term) return ALL_INVESTIGATIONS;

  return ALL_INVESTIGATIONS.filter((item) => {
    const labelLower = item.label.toLowerCase();
    const categoryLower = item.category.toLowerCase();

    // Generate initials (e.g. "Complete Blood Count" -> "cbc")
    const initials = labelLower
      .split(/[ \-\(\)]+/)
      .filter(w => w)
      .map(w => w[0])
      .join('');

    return (
      labelLower.includes(term) ||
      categoryLower.includes(term) ||
      initials.includes(term) ||
      (labelLower === 'liver function' && term === 'lft') ||
      (labelLower === 'kidney function' && term === 'kft')
    );
  });
};

export const groupInvestigationsByCategory = (investigations) => {
  return investigations.reduce((acc, inv) => {
    if (!acc[inv.categoryKey]) {
      acc[inv.categoryKey] = {
        label: inv.category,
        icon: inv.icon,
        items: []
      };
    }
    acc[inv.categoryKey].items.push(inv);
    return acc;
  }, {});
};

export const STRENGTH_OPTIONS = [
  { value: '50mg', label: '50mg' },
  { value: '100mg', label: '100mg' },
  { value: '250mg', label: '250mg' },
  { value: '500mg', label: '500mg' },
  { value: '750mg', label: '750mg' },
  { value: '1000mg', label: '1000mg' },
  { value: '5mcg', label: '5mcg' },
  { value: '25mcg', label: '25mcg' },
  { value: '50mcg', label: '50mcg' },
  { value: '100mcg', label: '100mcg' },
  { value: '250mcg', label: '250mcg' },
  { value: '500mcg', label: '500mcg' },
  { value: '5ml', label: '5ml' },
  { value: '10ml', label: '10ml' },
  { value: '15ml', label: '15ml' },
  { value: '20ml', label: '20ml' },
  { value: '30ml', label: '30ml' },
  { value: '100ml', label: '100ml' },
  { value: '200ml', label: '200ml' },
  { value: '250ml', label: '250ml' },
  { value: '500ml', label: '500ml' },
  { value: 'other', label: 'Other (custom)' }
];

export const TYPE_OPTIONS = [
  { value: 'tablet', label: 'Tablet' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'syrup', label: 'Syrup' },
  { value: 'injection', label: 'Injection' },
  { value: 'cream', label: 'Cream' },
  { value: 'ointment', label: 'Ointment' },
  { value: 'drops', label: 'Drops' },
  { value: 'inhaler', label: 'Inhaler' },
  { value: 'patch', label: 'Patch' },
  { value: 'suppository', label: 'Suppository' },
  { value: 'powder', label: 'Powder' },
  { value: 'gel', label: 'Gel' },
  { value: 'spray', label: 'Spray' },
  { value: 'other', label: 'Other (custom)' }
];

export const DURATION_OPTIONS = [
  { value: '3 days', label: '3 days' },
  { value: '5 days', label: '5 days' },
  { value: '7 days', label: '7 days' },
  { value: '10 days', label: '10 days' },
  { value: '14 days', label: '14 days' },
  { value: '15 days', label: '15 days' },
  { value: '21 days', label: '21 days' },
  { value: '30 days', label: '30 days' },
  { value: '1 month', label: '1 month' },
  { value: '3 months', label: '3 months' },
  { value: '6 months', label: '6 months' },
  { value: 'other', label: 'Other (custom)' }
];

export const INSTRUCTIONS_OPTIONS = [
  { value: 'before_meals', label: 'Before meals' },
  { value: 'after_meals', label: 'After meals' },
  { value: 'empty_stomach', label: 'Empty stomach' },
  { value: 'with_meals', label: 'With meals' },
  { value: 'at_bedtime', label: 'At bedtime' },
  { value: 'morning', label: 'In the morning' },
  { value: 'as_needed', label: 'As needed (PRN)' },
  { value: 'with_water', label: 'With plenty of water' },
  { value: 'with_food', label: 'With food' },
  { value: 'without_food', label: 'Without food' },
  { value: 'other', label: 'Other (custom)' }
];

export const UNIT_OPTIONS = [
  { value: 'mg', label: 'mg' },
  { value: 'mcg', label: 'mcg' },
  { value: 'g', label: 'g' },
  { value: 'kg', label: 'kg' },
  { value: 'ml', label: 'ml' },
  { value: 'L', label: 'L' },
  { value: 'IU', label: 'IU' },
  { value: '%', label: '%' },
  { value: 'units', label: 'Units' },
  { value: 'puffs', label: 'Puffs' },
  { value: 'drops', label: 'Drops' },
  { value: 'tablet', label: 'Tablet' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'vial', label: 'Vial' },
  { value: 'bottle', label: 'Bottle' },
  { value: 'strip', label: 'Strip' },
  { value: 'tube', label: 'Tube' },
  { value: 'pack', label: 'Pack' },
  { value: 'box', label: 'Box' },
  { value: 'sachet', label: 'Sachet' },
  { value: 'pessary', label: 'Pessary' },
  { value: 'suppository', label: 'Suppository' },
  { value: 'applicator', label: 'Applicator' },
  { value: 'spray', label: 'Spray' },
  { value: 'patch', label: 'Patch' },
  { value: 'other', label: 'Other (custom)' }
];

export const DURATION_UNIT_OPTIONS = [
  { value: 'days', label: 'days' },
  { value: 'weeks', label: 'weeks' },
  { value: 'months', label: 'months' },
  { value: 'years', label: 'years' }
];

export const MEDICINE_OPTIONS = [
  'Paracetamol', 'Ibuprofen', 'Amoxicillin', 'Azithromycin', 'Cetirizine',
  'Pantoprazole', 'Metformin', 'Amlodipine', 'Atorvastatin', 'Ondansetron'
];
